/**
 * The marketplace's own chrome — every marketplace page (public and signed
 * in) wears it, so the store reads as one consumer product rather than a
 * corner of the builder. Applies the user's theme itself (public pages render
 * outside AppLayout, which is what normally sets it).
 */
import { useEffect, type ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { useThemeStore } from '@/store/themeStore'
import './marketplace.css'

export function useMarketplaceTheme(): void {
  const theme = useThemeStore((s) => s.theme)
  useEffect(() => {
    document.body.className = theme === 'light' ? 'light-theme' : ''
  }, [theme])
}

export default function MarketplaceShell({ children, sell = false }: { children: ReactNode; sell?: boolean }) {
  useMarketplaceTheme()
  const { user, authStatus } = useAuth()
  const { pathname } = useLocation()
  const next = encodeURIComponent(window.location.pathname + window.location.search)
  return (
    <div className="mkt">
      <header className="mkt-header">
        <div className="mkt-header-in">
          <Link to="/marketplace" className="mkt-brand" aria-label="Twasta Marketplace home">
            <span className="mkt-brand-mark" aria-hidden="true">t</span>
            <span>twasta <small>{sell ? 'Marketplace · Sell' : 'Marketplace'}</small></span>
          </Link>
          <nav className="mkt-nav" aria-label="Marketplace">
            <NavLink to="/marketplace" end className={({ isActive }) => (isActive ? 'on' : '')}>Explore apps</NavLink>
            <NavLink to="/marketplace/build" className={({ isActive }) => (isActive ? 'on' : '')}>Get it built</NavLink>
            {authStatus === 'authed' && (
              <NavLink to="/marketplace/my-apps" className={({ isActive }) => (isActive ? 'on' : '')}>My apps</NavLink>
            )}
            <NavLink to="/marketplace/sell" className={({ isActive }) => (isActive ? 'on' : '')}>Sell your app</NavLink>
            {authStatus === 'authed' && (
              <>
                <span aria-hidden="true" style={{ width: 1, height: 22, background: 'var(--color-border)', margin: '0 6px', alignSelf: 'center' }} />
                <NavLink to="/marketplace/work" className={({ isActive }) => (isActive ? 'on' : '')}>Find work</NavLink>
                {/* Ready-made listings (/marketplace/seller) are folded in here; the page links to them. */}
                <NavLink to="/marketplace/builder" className={({ isActive }) => (isActive || pathname.startsWith('/marketplace/seller') ? 'on' : '')}>Your listings &amp; work</NavLink>
              </>
            )}
          </nav>
          <span className="mkt-spacer" />
          {authStatus === 'authed' && user ? (
            <div className="mkt-row" style={{ gap: 14 }}>
              <Link to="/workbench" className="mkt-muted" style={{ textDecoration: 'none', fontSize: 14 }}>Back to Twasta</Link>
              <span className="mkt-row" style={{ gap: 8 }}>
                <span aria-hidden="true" className="mkt-tile" style={{ width: 34, height: 34, borderRadius: 999, background: 'var(--color-accent)', fontSize: 13 }}>
                  {initials(user.user_name || user.email)}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <strong style={{ fontSize: 14 }}>{user.user_name}</strong>
                  {user.organization && <span className="mkt-faint">{user.organization}</span>}
                </span>
              </span>
            </div>
          ) : authStatus === 'loading' ? null : (
            <div className="mkt-row">
              <Link to={`/?next=${next}`} className="btn btn-ghost">Sign in</Link>
              <Link to="/register" className="btn btn-primary">Create free account</Link>
            </div>
          )}
        </div>
      </header>
      <main>{children}</main>
      <footer className="mkt-footer">
        <div>
          <span>© Twasta</span>
          <span className="mkt-row" style={{ gap: 18 }}>
            <Link to="/marketplace" className="mkt-muted">Explore</Link>
            <Link to="/marketplace/build" className="mkt-muted">Get it built</Link>
            <Link to="/marketplace/hosting" className="mkt-muted">Hosting prices</Link>
            <Link to="/marketplace/sell" className="mkt-muted">Sell your app</Link>
          </span>
        </div>
      </footer>
    </div>
  )
}

export function initials(name: string): string {
  const words = (name || '').split(/[\s@._-]+/).filter(Boolean)
  return ((words[0]?.[0] || 'U') + (words[1]?.[0] || '')).toUpperCase()
}

export function AppTile({ color, text, size = 48 }: { color?: string | null; text: string; size?: number }) {
  return (
    <span aria-hidden="true" className="mkt-tile" style={{ width: size, height: size, background: color || '#1F56D6', fontSize: Math.round(size * 0.34), borderRadius: Math.round(size * 0.27) }}>
      {text}
    </span>
  )
}

export function Check({ size = 16 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12l5 5L20 7" />
    </svg>
  )
}

export function Stepper({ value, onChange, min = 1, max = 10000, label }: { value: number; onChange: (n: number) => void; min?: number; max?: number; label: string }) {
  const clamp = (n: number) => Math.max(min, Math.min(max, Math.round(n || min)))
  return (
    <div className="mkt-stepper">
      <button type="button" aria-label={`Fewer ${label}`} onClick={() => onChange(clamp(value - 1))} disabled={value <= min}>−</button>
      <input aria-label={label} inputMode="numeric" value={value} onChange={(e) => onChange(clamp(Number(e.target.value.replace(/\D/g, ''))))} />
      <button type="button" aria-label={`More ${label}`} onClick={() => onChange(clamp(value + 1))} disabled={value >= max}>+</button>
    </div>
  )
}

export function Steps({ labels, current, done = false }: { labels: string[]; current: number; done?: boolean }) {
  return (
    <ol className="mkt-steps" aria-label="Progress">
      {labels.map((l, i) => {
        const idx = i + 1
        const isDone = done || idx < current
        const on = !done && idx === current
        return (
          <li key={l} className={isDone ? 'done' : on ? 'on' : ''} aria-current={on ? 'step' : undefined}>
            <span className="dot">{isDone ? '✓' : idx}</span>
            {l}
            {i < labels.length - 1 && <span className="bar" aria-hidden="true" />}
          </li>
        )
      })}
    </ol>
  )
}

export function LimitsList({ rows }: { rows: { key: string; label: string; help: string; tech?: string; value: string }[] }) {
  return (
    <div className="mkt-limits">
      {rows.map((r) => (
        <div key={r.key}>
          <div className="l">
            <strong>{r.label}</strong>
            <span className="mkt-muted" style={{ fontSize: 14 }}>{r.help}</span>
          </div>
          <div className="v">
            <strong>{r.value}</strong>
            {r.tech && <span className="mkt-faint">{r.tech}</span>}
          </div>
        </div>
      ))}
    </div>
  )
}
